import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getUserProfile, UserProfile } from '../services/userApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Field: React.FC<{ label: string; value?: string | number | boolean | null }> = ({
  label,
  value,
}) => {
  const display =
    value === null || value === undefined
      ? 'N/A'
      : typeof value === 'boolean'
      ? value
        ? 'Yes'
        : 'No'
      : String(value) || 'N/A';

  return (
    <div className='flex flex-col gap-0.5 py-2.5 border-b border-gray-100 last:border-0'>
      <span className='text-xs font-semibold uppercase tracking-wide text-gray-400'>{label}</span>
      <span className='text-sm text-gray-800 break-all'>{display}</span>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className='rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
    <div className='border-b border-gray-100 bg-gray-50 px-5 py-3'>
      <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-600'>{title}</h2>
    </div>
    <div className='px-5 divide-y divide-gray-100'>{children}</div>
  </div>
);

const LiveAccessUserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getUserProfile(userId);
      setProfile(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500 mb-4'>User profile not found or could not be loaded.</p>
          <button
            onClick={() => navigate(`/live-access/${userId}`)}
            className='rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
          >
            ← Back to Application
          </button>
        </div>
      </div>
    );
  }

  const displayName = profile.Name || profile.UserName || 'Unknown User';

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Back */}
        <button
          onClick={() => navigate(`/live-access/${userId}`)}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Application Detail
        </button>

        {/* Profile header */}
        <div className='mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            {/* Avatar */}
            {profile.ProfileImageURL ? (
              <img
                src={profile.ProfileImageURL}
                alt={displayName}
                className='h-20 w-20 rounded-full object-cover shrink-0 border-2 border-gray-200'
              />
            ) : (
              <div className='flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-3xl font-bold text-white shrink-0'>
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}

            <div className='flex-1 min-w-0'>
              <h1 className='text-2xl font-bold text-gray-900 truncate'>{displayName}</h1>
              {profile.UserName && (
                <p className='text-sm text-gray-500'>@{profile.UserName}</p>
              )}
              <p className='mt-0.5 text-xs font-mono text-gray-400'>{profile.UserID}</p>
              {profile.Bio && (
                <p className='mt-2 text-sm text-gray-600 line-clamp-2'>{profile.Bio}</p>
              )}
            </div>

            {/* Stats */}
            <div className='flex gap-6 text-center shrink-0'>
              <div>
                <p className='text-xl font-bold text-gray-900'>
                  {(profile.FollowersTotal ?? 0).toLocaleString()}
                </p>
                <p className='text-xs text-gray-500'>Followers</p>
              </div>
              <div>
                <p className='text-xl font-bold text-gray-900'>
                  {(profile.FollowingTotal ?? 0).toLocaleString()}
                </p>
                <p className='text-xs text-gray-500'>Following</p>
              </div>
              <div>
                <p className='text-xl font-bold text-gray-900'>
                  {profile.TotalVideos ?? 0}
                </p>
                <p className='text-xs text-gray-500'>Videos</p>
              </div>
            </div>
          </div>

          <div className='mt-4 flex flex-wrap gap-3'>
            <button
              onClick={() => navigate(`/live-access/${userId}`)}
              className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50'
            >
              Application Detail
            </button>
            <button
              onClick={() => navigate(`/live-access/${userId}/livestreams`)}
              className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50'
            >
              View Livestreams
            </button>
          </div>

          {/* Banner thumbnail */}
          {profile.BannerURL && (
            <img
              src={profile.BannerURL}
              alt='Banner'
              className='mt-4 h-28 w-full rounded-xl object-cover'
            />
          )}
        </div>

        {/* Data grid */}
        <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          <Section title='Identity'>
            <Field label='User ID' value={profile.UserID} />
            <Field label='Username' value={profile.UserName} />
            <Field label='Name' value={profile.Name} />
            <Field label='Account Type' value={profile.AccountType} />
            <Field label='Joined' value={formatDate(profile.createdAt)} />
            <Field label='Country' value={profile.Country} />
            <Field label='Location' value={profile.Location as string | undefined} />
          </Section>

          <Section title='Verification & Status'>
            <Field label='Account Status' value={profile.AccountStatus} />
            <Field label='Plus Verified' value={profile.IsPlusVerified} />
            <Field label='Is Verified' value={profile.IsVerified} />
            <Field label='Email Verified' value={profile.IsEmailVerified} />
            <Field label='Phone Verified' value={profile.IsPhoneVerified} />
            <Field label='Has Email' value={profile.HasEmail} />
            <Field label='Has Password' value={profile.HasPassword} />
            <Field label='Paid for Verification' value={profile.IsPaidForVerification} />
          </Section>

          <Section title='Subscription'>
            <Field label='Current Product' value={profile.CurrentProductId as string | undefined} />
            <Field label='Pending Product Change' value={profile.PendingProductChange as string | undefined} />
            <Field
              label='RevenueCat Expiration'
              value={formatDate(profile.RevenueCatOriginalExpiration as string | undefined)}
            />
          </Section>
        </div>
      </div>
    </div>
  );
};

export default LiveAccessUserProfile;
